/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ApplicationApk } from "./application-detail-types"

/**
 * Human-readable names for installers recognised by the console.
 *
 * The API provides the installer's package name rather than an enum, so unknown
 * values are kept as-is. ADB and package installers are included because they
 * identify sideloaded APKs even though they are not app stores.
 */
const INSTALLERS = new Map([
    ["com.android.vending", "Google Play"],
    ["com.sec.android.app.samsungapps", "Galaxy Store"],
    ["com.huawei.appmarket", "Huawei AppGallery"],
    ["com.amazon.venezia", "Amazon Appstore"],
    ["com.android.shell", "ADB"],
    ["com.google.android.packageinstaller", "Package installer"],
    ["com.android.packageinstaller", "Package installer"]
])

/**
 * Checks whether an APK belongs in the Malware list.
 *
 * Only the `MALWARE` threat level is included; lower threat levels remain in
 * Collected even when they have malware types.
 */
export function isMalware(apk: ApplicationApk): boolean {
    return apk.threatLevel?.name === "MALWARE"
}

/**
 * Checks whether the APK has a removal timestamp.
 *
 * The API can return active and removed APKs together; removed entries have
 * `timestampRemoved` set, while active entries do not.
 */
export function isRemoved(apk: ApplicationApk): boolean {
    return !!apk.timestampRemoved
}

/**
 * Converts an installer package name to a display name.
 *
 * Unknown package names are returned unchanged so the API value is not lost.
 *
 * @param installer - Installer package name, or `null` or `undefined` when unavailable.
 * @returns The display name, the original package name, or `null`.
 *
 * @example
 * installerName("com.android.vending") // "Google Play"
 * installerName("com.example.store") // "com.example.store"
 */
export function installerName(installer: string | null | undefined): string | null | undefined {
    if (!installer) return installer

    return INSTALLERS.get(installer) ?? installer
}
